"use client";

import { z } from "zod";
import { RegisterSalonServiceFormSchema } from "./schema";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormLabel } from "@/components/ui/form";
import { useTranslations } from "next-intl";
import hooks from "@/hooks";
import FormFields from "../Fields";
import Buttons from "@/components/core/Buttons";
import { TimePicker } from "@/components/ui/datetime-picker";
import { format, parse } from "date-fns";
import { UserJWTDecoded } from "@/types";
import { useToast } from "@/hooks/use-toast";
import { Vocation } from "@/actions/types";
import actions from "@/actions";
import { useState } from "react";
import OwnerCompanyServicesTable from "../../Table/OwnerCompanyServicesTable";
import { Plus } from "lucide-react";

type SalonService = z.infer<typeof RegisterSalonServiceFormSchema>;

interface IRegisterSalonServiceForm {
  user: UserJWTDecoded["user"];
  salonProfessions: Vocation[];
}

const RegisterSalonServiceForm = ({
  user,
  salonProfessions,
}: IRegisterSalonServiceForm) => {
  const t = useTranslations("Services.Register");
  const { toast } = useToast();

  const [services, setServices] = useState<Vocation[]>(salonProfessions);

  const form = useForm<SalonService>({
    resolver: zodResolver(RegisterSalonServiceFormSchema),
    defaultValues: {
      duration: "0000",
      name: "",
      value: 0,
      observation: "",
    },
  });

  const duration = form.watch().duration;

  const salonServiceService = hooks.useCreateSalonService({
    onSuccess: (data) => {
      toast({
        title: t("form.toasts.success.title"),
        description: t("form.toasts.success.description"),
        variant: "success",
      });
      form.reset({
        duration: "0000",
        name: "",
        value: 0,
        observation: "",
      });
    },
    onError: (err) => {
      toast({
        title: t("form.toasts.unexpectedError.title"),
        description: err.message || "Não foi possível criar o serviço.",
        variant: "warning",
      });
    },
  });

  async function onSubmit(data: SalonService) {
    await salonServiceService.mutateAsync({
      ...data,
      salon_owner_id: user.userid,
      value: data.value,
    });

    const updated = await actions.getSalonServicesByOwnerIdAction(user.userid);

    setServices(updated);
  }

  return (
    <>
      <div className="w-full flex flex-col gap-y-4 items-start justify-between bg-element-light rounded-2xl p-2 md:p-8">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="w-full flex flex-col items-center justify-center gap-y-4 p-4 sm:p-6"
          >
            <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:flex lg:flex-nowrap gap-4">
              <div className="w-full">
                <FormFields.InputFormField
                  form={form}
                  formName="name"
                  label={t("form.labels.name")}
                  placeholder={t("form.placeholder.name")}
                />
              </div>

              <div className="w-full sm:w-full lg:w-1/2">
                <FormFields.InputFormField
                  form={form}
                  formName="value"
                  label={t("form.labels.value")}
                  placeholder={t("form.placeholder.value")}
                  type="text"
                  noClearButton
                  mask="currency"
                />
              </div>

              <div className="flex flex-col mt-2 gap-2">
                <FormLabel>{t("form.labels.duration")}</FormLabel>
                <TimePicker
                  key={`duration-${form.formState.submitCount}`}
                  granularity="minute"
                  date={parse(
                    duration.replace(/^(\d{2})(\d{2})$/, "$1:$2"),
                    "HH:mm",
                    new Date(),
                  )}
                  onChange={(date) => {
                    if (date) {
                      const formattedTime = format(date, "HHmm");
                      form.setValue("duration", formattedTime);
                    }
                  }}
                />
                {form.formState.errors.duration && (
                  <p className="text-sm text-red-500">
                    {form.formState.errors.duration.message}
                  </p>
                )}
              </div>

              <div className="w-full">
                <FormFields.InputFormField
                  form={form}
                  formName="observation"
                  label={t("form.labels.observation")}
                  placeholder={t("form.placeholder.observation")}
                />
              </div>
            </div>

            <div className="w-full flex justify-end mt-2">
              <Buttons.DefaultButton
                type="submit"
                title={t("form.button")}
                className="w-full sm:w-auto h-12"
                isLoading={salonServiceService.isPending}
                rightElement={<Plus />}
              />
            </div>
          </form>
        </Form>
      </div>
      <div className="w-full flex flex-col gap-y-4 items-start justify-between bg-element-light rounded-2xl p-6 md:p-8">
        <h2 className="text-heading-xs">{t("salonServices")}</h2>
        <div className="w-full">
          <OwnerCompanyServicesTable data={services} user={user} />
        </div>
      </div>
    </>
  );
};

export default RegisterSalonServiceForm;
